'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useColorScheme } from '#pplx/frontend/aether/components/ColorSchemeProvider/useColorScheme.js';
import { trackEvent } from '#pplx/frontend/analytics/browser.js';
import { isCometBrowser } from '#pplx/frontend/comet/client/utils.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { FeatureFlagsContext } from '#pplx/frontend/experimentation/context.js';
import { getEppoAssignmentFlagKey } from '#pplx/frontend/experimentation/eppoSubjectTypes.js';
import { createFeatureFlagsPublisher } from '#pplx/frontend/experimentation/featureFlagsPublisher.js';
import { getEppoOverride } from '#pplx/frontend/experimentation/getEppoOverride.js';
import { EppoOpenFeatureProvider } from '#pplx/frontend/experimentation/openfeature/EppoOpenFeatureProvider.js';
import { eppoOpenFeatureClient, getCurrentEppoOpenFeatureContext, setEppoOpenFeatureProviderAndWait, } from '#pplx/frontend/experimentation/openfeature/openFeatureClient.js';
import { useOpenFeatureContext } from '#pplx/frontend/experimentation/openfeature/openFeatureContext.js';
import { getEppoOverrides, processEppoParameters, readEppoParameters, } from '#pplx/frontend/experimentation/storage.js';
import { registerConnectorGoProxySelector } from '#pplx/frontend/fetcher/connectorRouting.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { ProviderEvents, ProviderStatus } from '@openfeature/web-sdk';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const EVENT_NAME = 'experiment assignment';
const OPEN_FEATURE_CLIENT_EVENTS = [
    ProviderEvents.Ready,
    ProviderEvents.Error,
    ProviderEvents.Stale,
    ProviderEvents.ConfigurationChanged,
    ProviderEvents.Reconciling,
    ProviderEvents.ContextChanged,
];
const logAssignmentFn = (isPerplexityBrowser, session, colorScheme, pagePropsHostname) => {
    return (assignment) => {
        const subjectAttributes = {
            ...assignment.subjectAttributes,
        };
        delete subjectAttributes.user_email;
        delete subjectAttributes.userEmail;
        trackEvent({
            name: EVENT_NAME,
            data: {
                subjectType: assignment.subjectAttributes.subjectType,
                pagePropsHostname,
                colorScheme,
                isPerplexityBrowser,
                ...assignment,
                subjectAttributes,
            },
            user: {
                id: session?.user?.id,
                subscription_status: session?.user?.subscription_status,
            },
        });
    };
};
function getAssignmentValueKind(defaultValue) {
    if (typeof defaultValue === 'boolean') {
        return 'boolean';
    }
    if (typeof defaultValue === 'string') {
        return 'string';
    }
    if (typeof defaultValue === 'number') {
        return 'number';
    }
    if (typeof defaultValue === 'object' && defaultValue !== null) {
        return 'object';
    }
    return undefined;
}
function resolveOpenFeatureVariation({ client, eppoProvider, flag, defaultValue, evaluationContext, }) {
    const valueKind = getAssignmentValueKind(defaultValue);
    if (!valueKind) {
        return defaultValue;
    }
    if (evaluationContext) {
        if (!eppoProvider) {
            return defaultValue;
        }
        return eppoProvider.resolveValue(flag, defaultValue, {
            ...getCurrentEppoOpenFeatureContext(),
            ...evaluationContext,
        });
    }
    switch (valueKind) {
        case 'boolean':
            return client.getBooleanValue(flag, defaultValue);
        case 'string':
            return client.getStringValue(flag, defaultValue);
        case 'number':
            return client.getNumberValue(flag, defaultValue);
        case 'object':
            return client.getObjectValue(flag, defaultValue);
    }
}
function getEvaluationCacheKey(flag, defaultValue, evaluationContext) {
    const valueKind = getAssignmentValueKind(defaultValue);
    return valueKind
        ? JSON.stringify([flag, valueKind, defaultValue, evaluationContext ?? null])
        : undefined;
}
function isOpenFeatureProviderLoading(providerStatus) {
    return (providerStatus === ProviderStatus.NOT_READY || providerStatus === ProviderStatus.RECONCILING);
}
function getOpenFeatureEvaluationState({ hasLoadedUserInfo, providerStatus, isConfigured, shouldDeferAssignments, isSessionLoading, }) {
    if (shouldDeferAssignments || !isConfigured) {
        return 'fail-open';
    }
    if (providerStatus === ProviderStatus.ERROR) {
        return 'fail-open';
    }
    if (isSessionLoading || !hasLoadedUserInfo) {
        return 'loading';
    }
    if (!isOpenFeatureProviderLoading(providerStatus)) {
        return 'ready';
    }
    return 'loading';
}
function canEvaluateOpenFeature(state) {
    return state === 'ready';
}
export const FeatureFlagsProvider = ({ children, hostname, appClientVersion, session, isSessionLoading = false, cfCountry, effectivePhase, deferAssignmentsUntilHydrated = false, }) => {
    const logAssignmentRef = useRef(() => undefined);
    const eppoProviderRef = useRef(undefined);
    const evaluationCacheRef = useRef(new Map());
    const [eppoClientSnapshot, setEppoClientSnapshot] = useState(() => ({
        providerStatus: ProviderStatus.NOT_READY,
    }));
    const isComet = isCometBrowser();
    const { env: { isProduction, isStaging, serverEnvironment }, } = useWebPlatform();
    // Allow local development to use the Production Eppo environment.
    const eppoUseProduction = import.meta.env.VITE_EPPO_USE_PRODUCTION === 'true';
    const useProductionEppo = isProduction || isStaging || eppoUseProduction;
    const tenantSnapshot = serverEnvironment === 'single-tenant' || !BUILD_CAPABILITIES.remoteExperiments;
    const primaryApiKey = tenantSnapshot
        ? 'tenant-snapshot'
        : useProductionEppo
            ? import.meta.env.VITE_EPPO_AGI_FRONTEND_PROD_KEY
            : import.meta.env.VITE_EPPO_AGI_FRONTEND_TEST_KEY;
    // Feature flags can sit above the visual provider at an application composition root.
    // Color scheme only enriches assignment telemetry, so its absence must not block evaluation.
    const colorScheme = useColorScheme()?.colorScheme;
    const searchParams = useSearchParams();
    const [hasProcessedUrlOverrides, setHasProcessedUrlOverrides] = useState(false);
    const [hasHydrated, setHasHydrated] = useState(false);
    const shouldDeferAssignments = deferAssignmentsUntilHydrated && !hasHydrated;
    const { appliedEppoUserId, hasLoadedUserInfo } = useOpenFeatureContext({
        appClientVersion,
        cfCountry,
        effectivePhase,
        session,
    });
    useEffect(() => {
        setHasHydrated(true);
    }, []);
    useEffect(() => {
        const abortController = new AbortController();
        const syncEppoClientSnapshot = () => {
            evaluationCacheRef.current.clear();
            setEppoClientSnapshot({
                providerStatus: eppoOpenFeatureClient.providerStatus,
            });
        };
        // The OpenFeature client is an external mutable singleton. Start this
        // provider mount as not-ready, then mirror lifecycle events so hooks
        // re-evaluate after readiness and context changes without reading a stale
        // status from an earlier registration. ContextChanged can keep the same
        // providerStatus, so each event stores a fresh snapshot object.
        OPEN_FEATURE_CLIENT_EVENTS.forEach((event) => {
            eppoOpenFeatureClient.addHandler(event, syncEppoClientSnapshot, {
                signal: abortController.signal,
            });
        });
        return () => abortController.abort();
    }, []);
    const logAssignment = useCallback((assignment) => {
        logAssignmentFn(isComet, session, colorScheme, hostname)(assignment);
    }, [session, isComet, colorScheme, hostname]);
    logAssignmentRef.current = logAssignment;
    // Process URL parameters after initialization
    useEffect(() => {
        if (hasProcessedUrlOverrides || !searchParams) {
            return;
        }
        const success = processEppoParameters(session?.user?.email, searchParams, isProduction);
        if (success) {
            setHasProcessedUrlOverrides(true);
        }
    }, [searchParams, session?.user?.email, hasProcessedUrlOverrides, isProduction]);
    useEffect(() => {
        if (!primaryApiKey) {
            return;
        }
        const provider = new EppoOpenFeatureProvider({
            apiKey: primaryApiKey,
            logAssignment: (assignment) => logAssignmentRef.current(assignment),
            tenantSnapshot,
        });
        eppoProviderRef.current = provider;
        // This module assumes exactly one FeatureFlagsProvider mount per document:
        // Eppo-bearing roots do not use StrictMode, and primaryApiKey is a build-time
        // constant, so this effect fires once. Concurrent loadEppoAssignmentClient
        // runs would race on the Eppo singleton: a superseded run's offlineInit
        // fallback has no readiness guard and could replace a ready client's fresh
        // config with its stale snapshot. Serialize init/fallback globally before
        // adding a second root or a re-registration path.
        void setEppoOpenFeatureProviderAndWait(provider).catch(() => undefined);
        return () => {
            if (eppoProviderRef.current === provider) {
                eppoProviderRef.current = undefined;
            }
        };
    }, [primaryApiKey, tenantSnapshot]);
    const getCurrentOpenFeatureEvaluationState = useCallback(() => getOpenFeatureEvaluationState({
        hasLoadedUserInfo,
        providerStatus: eppoClientSnapshot.providerStatus,
        isConfigured: Boolean(primaryApiKey),
        shouldDeferAssignments,
        isSessionLoading,
    }), [
        eppoClientSnapshot,
        hasLoadedUserInfo,
        primaryApiKey,
        shouldDeferAssignments,
        isSessionLoading,
    ]);
    const isOpenFeatureFlagReady = useCallback(() => getCurrentOpenFeatureEvaluationState() !== 'loading', [getCurrentOpenFeatureEvaluationState]);
    // apply the page override before children can preload the previously enabled path.
    const pageOverrides = useMemo(() => !hasProcessedUrlOverrides && searchParams
        ? readEppoParameters(session?.user?.email, searchParams, isProduction)
        : undefined, [hasProcessedUrlOverrides, searchParams, session?.user?.email, isProduction]);
    const getFlagOverride = useCallback(function ({ flag, defaultValue }) {
        const eppoFlag = getEppoAssignmentFlagKey(flag);
        const overrides = getEppoOverrides();
        return getEppoOverride({
            flag: eppoFlag,
            override: pageOverrides?.[eppoFlag] ?? overrides[eppoFlag],
            defaultValue,
        });
    }, [pageOverrides]);
    const getFlagValue = useCallback(function ({ flag, defaultValue, evaluationContext, skip }) {
        if (skip) {
            return defaultValue;
        }
        try {
            const { shouldUseOverride, value } = getFlagOverride({ flag, defaultValue });
            if (shouldUseOverride) {
                return value ?? defaultValue;
            }
            if (!canEvaluateOpenFeature(getCurrentOpenFeatureEvaluationState())) {
                return defaultValue;
            }
            if (evaluationContext && !eppoProviderRef.current?.isReady()) {
                return defaultValue;
            }
            const cacheKey = getEvaluationCacheKey(flag, defaultValue, evaluationContext);
            if (!cacheKey) {
                return defaultValue;
            }
            const evaluationCache = evaluationCacheRef.current;
            if (evaluationCache.has(cacheKey)) {
                return evaluationCache.get(cacheKey);
            }
            // Keep imperative callers fail-open; some callsites intentionally do
            // not attach a catch because the old fetchVariation helper swallowed.
            const variation = resolveOpenFeatureVariation({
                client: eppoOpenFeatureClient,
                eppoProvider: eppoProviderRef.current,
                flag,
                defaultValue,
                evaluationContext,
            });
            evaluationCache.set(cacheKey, variation);
            return variation;
        }
        catch {
            return defaultValue;
        }
    }, [getFlagOverride, getCurrentOpenFeatureEvaluationState]);
    const userId = session?.user?.id;
    useLayoutEffect(() => registerConnectorGoProxySelector(() => {
        if (!userId ||
            isSessionLoading ||
            appliedEppoUserId !== userId ||
            getCurrentOpenFeatureEvaluationState() !== 'ready')
            return undefined;
        return getFlagValue({ flag: 'connector-service-go-proxy-browser', defaultValue: false })
            ? userId
            : undefined;
    }), [
        userId,
        isSessionLoading,
        appliedEppoUserId,
        getCurrentOpenFeatureEvaluationState,
        getFlagValue,
    ]);
    const snapshot = useMemo(() => ({
        appliedEppoUserId,
        // URL overrides are stored in an effect after children mount; this makes
        // existing hooks re-check the override store once that one-shot work runs.
        hasProcessedUrlOverrides,
        getFlagOverride,
        getFlagValue,
        hasLoadedUserInfo,
        isOpenFeatureFlagReady,
    }), [
        appliedEppoUserId,
        getFlagOverride,
        getFlagValue,
        hasLoadedUserInfo,
        hasProcessedUrlOverrides,
        isOpenFeatureFlagReady,
    ]);
    const [publisher] = useState(() => createFeatureFlagsPublisher(snapshot));
    publisher.update(snapshot);
    useLayoutEffect(() => publisher.notify(), [publisher, snapshot]);
    return (_jsx(FeatureFlagsContext.Provider, { value: publisher.value, children: children }));
};
//# sourceMappingURL=provider.js.map